/**
 * 服务器监控 · 用户行为异常（疑似爬取）— 只读响应形状。
 *
 * 接口：`GET /api/v1/server-monitor/user-crawl?service=`（仅 root / admin），数据来自 cron 写的 KV 状态
 * `v1:admin:server-monitor:user-crawl-state:<service>`：近 24h 达过阈值的用户 + 最近一轮评估摘要 + 生效阈值。
 * 口径：按 userId 统计 C 端 `request.complete` 日志里 `GET /api/v1/chapters/:id/content` 的请求数与其中 HTTP 422 数
 * （C 端业务拒绝统一 422：限流、付费墙锁章、不存在、校验等，日志里分不开）。计数为 Telemetry 估算值。
 * 字段 snake_case；时间一律 ISO 8601（UTC `Z`）。
 */
import { z } from 'zod';
import { ServerMonitorServiceSchema } from './server-monitor';

export const ServerMonitorUserCrawlQuerySchema = z.object({
  /** 缺省 = 本环境监控目标；不在本环境可查看列表 → 422 FORBIDDEN_OPERATION */
  service: ServerMonitorServiceSchema.optional(),
});
export type ServerMonitorUserCrawlQuery = z.infer<typeof ServerMonitorUserCrawlQuerySchema>;

export const ServerMonitorUserCrawlThresholdsSchema = z.object({
  window_minutes: z.number().int().positive(),
  /** 每用户窗口内正文请求数达到即告警 */
  min_content_requests: z.number().int().positive(),
  /** 每用户窗口内被拒（HTTP 422：限流 / 锁章等）数达到即告警 */
  min_rejected: z.number().int().positive(),
  /** 同一用户告警冷却 */
  cooldown_minutes: z.number().int().positive(),
});
export type ServerMonitorUserCrawlThresholds = z.infer<
  typeof ServerMonitorUserCrawlThresholdsSchema
>;

export const ServerMonitorUserCrawlHitSchema = z.object({
  user_id: z.string(),
  /** 本段爬取首次达阈值的时间（距上次达标超过冷却期即重算）*/
  first_seen_at: z.string(),
  last_seen_at: z.string(),
  /** 最近一次成功推送飞书的时间；null = 尚未送达 */
  last_notified_at: z.string().nullable(),
  notification_count: z.number().int().nonnegative(),
  /** 待推送（新命中或冷却后仍在爬，下一轮发）*/
  pending: z.boolean(),
  /** 最近一次达标那轮的窗口计数（Telemetry 估算值）*/
  content_requests: z.number().int().nonnegative(),
  /** content_requests 只是下界（该用户因被拒达阈值并入、没进按请求数排序的前 N 名，取的是被拒数）*/
  content_requests_lower_bound: z.boolean(),
  rejected: z.number().int().nonnegative(),
  /** 计数来自 Cloudflare 采样，是估算 */
  sampled: z.boolean(),
  window_from: z.string(),
  window_to: z.string(),
  /** 本段爬取中单窗口最大正文请求数 */
  peak_content_requests: z.number().int().nonnegative(),
  /** 推送时现查的去重章节数；null = 未查到 */
  distinct_chapters: z.number().int().nonnegative().nullable(),
  /** 去重章节达到查询上限（实际 ≥ distinct_chapters）*/
  distinct_chapters_truncated: z.boolean(),
  /** 涉及书数；null = 未查到 */
  novels: z.number().int().nonnegative().nullable(),
});
export type ServerMonitorUserCrawlHit = z.infer<typeof ServerMonitorUserCrawlHitSchema>;

export const ServerMonitorUserCrawlSchema = z.object({
  service: ServerMonitorServiceSchema,
  /** 本环境 cron 是否评估这个 service；false 时 hits / last_tick 为空（状态在该环境后台查看）*/
  is_monitor_target: z.boolean(),
  /** Telemetry 凭证与飞书配置齐全（缺任一项 cron 不跑）*/
  configured: z.boolean(),
  thresholds: ServerMonitorUserCrawlThresholdsSchema,
  thresholds_source: z.enum(['environment', 'defaults']),
  last_tick: z
    .object({
      at: z.string(),
      /** false = 查询失败；true 时 error_code 非空表示口径降级（照常判定，计数可能不准）*/
      ok: z.boolean(),
      error_code: z.string().nullable(),
      /** 口径降级时的形状校验摘要；其余为 null */
      warning: z.string().nullable(),
      /** 参与判定的用户数 */
      candidates: z.number().int().nonnegative(),
      /** 窗口内正文请求总量（估算）；查询失败轮为 null */
      total_content_requests: z.number().int().nonnegative().nullable(),
      breaches: z.number().int().nonnegative(),
      truncated: z.boolean(),
      sampled: z.boolean(),
    })
    .nullable(),
  /** 非 null = 查询正在失败（首个失败轮时间）；查询已恢复、只差恢复通知没送达时为 null */
  failing_since: z.string().nullable(),
  /** 非 null = 口径降级中（形状校验不过的首轮时间）：照常判定推送，计数可能不准 */
  degraded_since: z.string().nullable(),
  /** 近 24h 命中用户，最近命中在前 */
  hits: z.array(ServerMonitorUserCrawlHitSchema),
});
export type ServerMonitorUserCrawl = z.infer<typeof ServerMonitorUserCrawlSchema>;
